import { createRootRoute, createRoute, createRouter, redirect, Outlet, useRouterState } from '@tanstack/react-router';
import { AppLayout } from './components/Layout.tsx';
import { LoginPage } from './pages/auth/Login.tsx';
import { AcceptInvitePage } from './pages/auth/AcceptInvite.tsx';
import { DashboardPage } from './pages/Dashboard.tsx';
import { ScreensPage, type ScreensSearch } from './pages/screens/ScreensPage.tsx';
import { MediaPage, type MediaSearch } from './pages/media/MediaPage.tsx';
import { PlaylistsPage } from './pages/playlists/PlaylistsPage.tsx';
import { PlaylistEditor } from './pages/playlists/PlaylistEditor.tsx';
import { SchedulesPage, type SchedulesSearch } from './pages/schedules/SchedulesPage.tsx';
import { SettingsPage } from './pages/settings/SettingsPage.tsx';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage.tsx';
import { AdminPage } from './pages/admin/AdminPage.tsx';
import type { ScreenStatus } from '@seencast/shared';

const asString = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
const asBool = (v: unknown): true | undefined => (v === true || v === 'true' ? true : undefined);

/**
 * Root chooses between the bare public pages (login, accept-invite) and the
 * authenticated app shell, keeping all route paths flat (no id prefix).
 */
function RootShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isPublic = pathname === '/login' || pathname.startsWith('/accept-invite');
  return isPublic ? <Outlet /> : <AppLayout />;
}

const rootRoute = createRootRoute({ component: RootShell });

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage });
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: AcceptInvitePage,
});

const dashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: DashboardPage });

const screensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/screens',
  component: ScreensPage,
  validateSearch: (s: Record<string, unknown>): ScreensSearch => ({
    screen: asString(s.screen),
    pair: asBool(s.pair),
    code: asString(s.code),
    groups: asBool(s.groups),
    status: asString(s.status) as ScreenStatus | undefined,
    q: asString(s.q),
    group: asString(s.group),
  }),
});

// Deep-link the player's QR points at: /screens/pair?code=XXXXXX → open the modal.
const screensPairRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/screens/pair',
  validateSearch: (s: Record<string, unknown>): { code?: string } => ({ code: asString(s.code) }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/screens', search: { pair: true, code: search.code } });
  },
});

const mediaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/media',
  component: MediaPage,
  validateSearch: (s: Record<string, unknown>): MediaSearch => ({
    media: asString(s.media),
    kind: asString(s.kind) as MediaSearch['kind'],
    q: asString(s.q),
    addUrl: asBool(s.addUrl),
    create: asBool(s.create),
    template: asString(s.template),
  }),
});

const playlistsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/playlists', component: PlaylistsPage });
const playlistEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/playlists/$playlistId',
  component: PlaylistEditor,
});

const schedulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/schedules',
  component: SchedulesPage,
  validateSearch: (s: Record<string, unknown>): SchedulesSearch => ({
    edit: asString(s.edit),
    create: asBool(s.create),
  }),
});

const analyticsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/analytics', component: AnalyticsPage });
const settingsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/settings', component: SettingsPage });
const adminRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin', component: AdminPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  acceptInviteRoute,
  dashboardRoute,
  screensRoute,
  screensPairRoute,
  mediaRoute,
  playlistsRoute,
  playlistEditorRoute,
  schedulesRoute,
  analyticsRoute,
  settingsRoute,
  adminRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
